// Rich content pages: Platform, Tokenized Assets, Partnerships, Compliance, Resources
import { Link } from "react-router-dom";
import {
  ArrowRight, Building2, Coins, Layers, Cpu, ShieldCheck, ScrollText, Users,
  Globe, TrendingUp, FileCheck2, Fingerprint, Scale, Landmark, Store, Zap,
  Search, Lock, Radio, Handshake, FileText, BookOpen, Download, Newspaper,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/shared";

const Section = ({ children, className = "" }) => <section className={`py-16 md:py-20 ${className}`}>{children}</section>;

const Hero = ({ kicker, title, blue, sub, ctas }) => (
  <section className="pt-40 pb-16 relative">
    <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[600px] blue-glow pointer-events-none" />
    <div className="container-auvra relative z-10 max-w-4xl">
      <Reveal><span className="kicker mb-4 block">{kicker}</span></Reveal>
      <Reveal i={1}>
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05]">
          {title} <span className="text-auvra-blue">{blue}</span>
        </h1>
      </Reveal>
      <Reveal i={2}><p className="mt-6 text-lg text-secondary-auvra leading-relaxed">{sub}</p></Reveal>
      {ctas && <Reveal i={3}><div className="mt-8 flex flex-wrap gap-3">{ctas}</div></Reveal>}
    </div>
  </section>
);

const CtaBar = ({ primary = "Start Investing", primaryHref = "/signup", secondary = "Read Whitepaper", secondaryHref = "/whitepaper" }) => (
  <Section>
    <div className="container-auvra">
      <div className="glass rounded-3xl p-10 md:p-14 border border-auvra-blue/20 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] blue-glow pointer-events-none" />
        <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-3xl md:text-4xl font-semibold tracking-tight">Ready to build with AUVRA?</h3>
            <p className="text-secondary-auvra mt-3">Institutional infrastructure, on-chain transparency, and a growing ecosystem of tokenized real world assets.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to={primaryHref} className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">{primary} <ArrowRight className="w-4 h-4" /></Link>
            <Link to={secondaryHref} className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-medium px-6 py-3 rounded-full transition-all hover:bg-white/5">{secondary}</Link>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

const Grid = ({ items, columns = 3 }) => (
  <div className={`grid gap-4 md:grid-cols-2 lg:grid-cols-${columns}`}>
    {items.map((it, i) => (
      <Reveal key={it.t} i={i % columns}>
        <div className="glass rounded-2xl p-8 h-full hover:border-auvra-blue/30 transition-colors">
          <it.I className="w-6 h-6 text-auvra-blue mb-5" />
          <h4 className="text-lg font-medium">{it.t}</h4>
          <p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{it.d}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

const SectionHeader = ({ kicker, title, sub, center = false }) => (
  <div className={`mb-12 ${center ? "text-center max-w-2xl mx-auto" : "max-w-3xl"}`}>
    <Reveal><span className="kicker mb-4 block">{kicker}</span></Reveal>
    <Reveal i={1}><h2 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">{title}</h2></Reveal>
    {sub && <Reveal i={2}><p className="mt-6 text-secondary-auvra leading-relaxed">{sub}</p></Reveal>}
  </div>
);

// ========================================
//  PLATFORM
// ========================================
export function Platform({ onCta }) {
  return (
    <>
      <Navbar onCta={onCta} />
      <main>
        <Hero kicker="Platform" title="One institutional rail for the" blue="entire asset lifecycle"
          sub="AUVRA unifies issuance, custody, compliance and settlement into a single institutional-grade platform — bring real-world value on-chain without stitching together vendors."
          ctas={<>
            <Link to="/signup" className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">Open Investor Account <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/whitepaper" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-medium px-6 py-3 rounded-full transition-all hover:bg-white/5">Read Whitepaper</Link>
          </>}
        />

        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Core Modules" title="A complete tokenization stack" sub="Every module is built for institutional standards — from issuance to secondary trading, with the AUVRA compliance engine at the core." />
            <Grid columns={3} items={[
              { I: Layers, t: "Issuance Engine", d: "Configure token supply, legal wrapper, jurisdiction, transfer rules and distribution schedule. Deploy compliant BEP-20 tokens in hours, not months." },
              { I: ShieldCheck, t: "Compliance Engine", d: "KYC/AML/KYB, accredited-investor gating, sanctions & PEP screening, on-chain transfer restrictions and continuous monitoring." },
              { I: Landmark, t: "Custody Integration", d: "Institutional-grade custody compatibility with multi-signature treasury management and audited operational controls." },
              { I: Store, t: "Secondary Market", d: "Compliant secondary market infrastructure with on-chain matching, dealer relationships and periodic redemption windows." },
              { I: TrendingUp, t: "Investor Dashboard", d: "Real-time portfolio, distributions, tax reports, KYC and transaction ledger for individuals and institutions." },
              { I: Cpu, t: "Developer APIs", d: "REST + Webhook APIs for asset managers to integrate issuance and portfolio flows into existing back-office systems." },
            ]} />
          </div>
        </Section>

        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Infrastructure" title="Built on BNB Smart Chain" sub="A high-throughput, cost-efficient, EVM-compatible L1 supports the AUVRA operating model with sub-3s finality and 24/7 on-chain operations." />
            <div className="grid md:grid-cols-4 gap-4">
              {[["<3s", "Average Finality"], ["Low", "Transaction Costs"], ["EVM", "Compatible"], ["24/7", "On-Chain Ops"]].map(([n, l], i) => (
                <Reveal key={l} i={i}>
                  <div className="glass rounded-2xl p-8 text-center">
                    <div className="text-4xl font-semibold text-auvra-blue">{n}</div>
                    <div className="text-xs uppercase tracking-wider text-secondary-auvra mt-2">{l}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Lifecycle" title="From onboarding to distribution" sub="A four-step institutional workflow — engineered for compliance, transparency and long-term relationships." />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["01", "Onboard", "Register, verify identity (KYC/KYB), attest eligibility and connect institutional custody."],
                ["02", "Fund", "Deposit crypto via BEP20 or TRC20. Settle instantly into virtual wallet."],
                ["03", "Subscribe", "Allocate to curated offerings across real estate, credit, infrastructure and treasuries."],
                ["04", "Receive", "Cash distributions arrive on-chain. Reports, tax exports and secondary liquidity are available in one dashboard."],
              ].map(([n, t, d], i) => (
                <Reveal key={n} i={i}>
                  <div className="glass rounded-2xl p-6 h-full">
                    <div className="text-xs tracking-[0.2em] text-auvra-blue mb-3">{n}</div>
                    <h4 className="font-medium">{t}</h4>
                    <p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        <CtaBar />
      </main>
      <Footer />
    </>
  );
}

// ========================================
//  TOKENIZED ASSETS
// ========================================
export function TokenizedAssets({ onCta }) {
  const CLASSES = [
    { I: Building2, t: "Real Estate", d: "Prime commercial and residential real estate — income-producing, independently valued, tokenized with on-chain distributions." },
    { I: Coins, t: "Private Credit", d: "Senior secured lending to mid-market operating companies with quarterly income and low correlation to public markets." },
    { I: Landmark, t: "Fixed Income", d: "Short-duration treasury baskets, corporate credit and structured products tokenized for daily NAV and on-chain redemption." },
    { I: Zap, t: "Infrastructure", d: "Utility-scale solar, transport, digital infrastructure with long-dated PPA revenues and ESG-aligned reporting." },
    { I: Globe, t: "Commodities", d: "Rules-based baskets across energy, agriculture and metals — tokenized with weekly rebalancing." },
    { I: TrendingUp, t: "Venture & Growth", d: "Curated early-stage exposure across AUVRA committee-approved sectors with periodic liquidity windows." },
  ];
  return (
    <>
      <Navbar onCta={onCta} />
      <main>
        <Hero kicker="Tokenized Assets" title="Real value," blue="made programmable"
          sub="Real estate, credit, infrastructure, commodities and treasuries — fractionalized into compliant digital tokens with enforceable rights and transparent on-chain records." />
        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Asset Classes" title="Six investable universes" sub="Each asset class is engineered for institutional standards: legal enforceability, transparent valuation, and clear cash-flow mechanics." />
            <Grid columns={3} items={CLASSES} />
          </div>
        </Section>

        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Anatomy" title="What sits under every AUVRA token" sub="A tokenized offering is more than a smart contract. It's a legal wrapper, an economic engine and a compliance framework — all aligned to the underlying asset." />
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { I: ScrollText, t: "Legal Wrapper", d: "Jurisdiction-appropriate SPV or fund structure holding the underlying asset, with token-holder rights clearly documented." },
                { I: Fingerprint, t: "On-chain Identity", d: "Whitelisted, KYC-verified investors with transfer restrictions enforced on the token itself." },
                { I: FileCheck2, t: "Economic Rights", d: "Cash-flow distributions, redemption windows, secondary transfers — encoded and executed on-chain." },
                { I: Scale, t: "Governance & Reporting", d: "Independent valuation, periodic auditor confirmation and transparent operational reports." },
              ].map((it, i) => (
                <Reveal key={it.t} i={i}>
                  <div className="glass rounded-2xl p-8 h-full flex gap-5 items-start">
                    <div className="w-12 h-12 rounded-xl bg-auvra-blue/10 border border-auvra-blue/20 flex items-center justify-center flex-shrink-0"><it.I className="w-5 h-5 text-auvra-blue" /></div>
                    <div><h4 className="font-medium">{it.t}</h4><p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{it.d}</p></div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        <CtaBar primary="Browse Offerings" primaryHref="/investors" />
      </main>
      <Footer />
    </>
  );
}

// ========================================
//  PARTNERSHIPS
// ========================================
export function Partnerships({ onCta }) {
  return (
    <>
      <Navbar onCta={onCta} />
      <main>
        <Hero kicker="Partnerships" title="Build on institutional" blue="tokenization infrastructure"
          sub="Banks, asset managers, funds and fintechs integrate AUVRA to launch compliant tokenization products under their own brand — powered by our platform, compliance engine and custody integrations."
          ctas={<Link to="/contact" className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">Talk to Partnerships <ArrowRight className="w-4 h-4" /></Link>}
        />

        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Who We Partner With" title="Institutional partners across the stack" />
            <Grid columns={3} items={[
              { I: Landmark, t: "Asset Managers", d: "Launch tokenized share classes and structured products with institutional custody, on-chain distributions and compliant transfer rules." },
              { I: Building2, t: "Real Estate Sponsors", d: "Tokenize property portfolios, unlock global capital and manage distributions programmatically." },
              { I: Coins, t: "Credit Originators", d: "Fund credit facilities on-chain with senior secured structures, quarterly reporting and secondary market access." },
              { I: Handshake, t: "Fintech Platforms", d: "White-label AUVRA infrastructure to offer tokenized investment products to your users under your own brand." },
              { I: Cpu, t: "Technology Partners", d: "Integrate custody, KYC, analytics or oracle services into the AUVRA operating platform." },
              { I: Users, t: "Family Offices & Networks", d: "Curated allocation programs across the AUVRA marketplace with dedicated relationship coverage." },
            ]} />
          </div>
        </Section>

        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Engagement Models" title="Three ways to work with AUVRA" />
            <div className="grid md:grid-cols-3 gap-4">
              {[
                ["Issuer", "Bring your asset to AUVRA for full-stack tokenization — legal, compliance, tech and distribution.", ["Legal wrapper", "Smart contract issuance", "Investor marketplace", "Ongoing lifecycle"]],
                ["White-label", "Embed AUVRA's platform into your product and offer tokenized investing to your users under your own brand.", ["Branded UI/UX", "Compliance engine", "Custody integration", "Reporting APIs"]],
                ["Strategic", "Deep integrations for banks, custodians, and market-infrastructure partners with joint go-to-market.", ["Multi-market coverage", "Custody rails", "Regulatory dialogue", "Joint distribution"]],
              ].map(([t, d, items], i) => (
                <Reveal key={t} i={i}>
                  <div className="glass rounded-2xl p-8 h-full">
                    <h4 className="text-xl font-semibold tracking-tight mb-2">{t}</h4>
                    <p className="text-sm text-secondary-auvra leading-relaxed mb-5">{d}</p>
                    <ul className="space-y-2 text-sm">
                      {items.map((x) => <li key={x} className="flex gap-2"><ArrowRight className="w-4 h-4 text-auvra-blue mt-0.5 flex-shrink-0" /> {x}</li>)}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        <CtaBar primary="Contact Partnerships" primaryHref="/contact" />
      </main>
      <Footer />
    </>
  );
}

// ========================================
//  COMPLIANCE
// ========================================
export function Compliance({ onCta }) {
  return (
    <>
      <Navbar onCta={onCta} />
      <main>
        <Hero kicker="Compliance" title="Compliant" blue="by construction"
          sub="Every AUVRA issuance embeds jurisdiction-aware rules — KYC/AML, transfer restrictions, accreditation and audit trails — enforced automatically on-chain." />
        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Framework" title="A defense-in-depth compliance architecture" sub="Compliance is not bolted on. It's integrated across identity, transfer, treasury and reporting layers." />
            <Grid columns={2} items={[
              { I: Fingerprint, t: "Identity (KYC / KYB / AML)", d: "Onboarding across individuals, entities and beneficial owners with document verification, sanctions & PEP screening, and continuous monitoring." },
              { I: Lock, t: "On-chain Transfer Rules", d: "Whitelisted transfers, jurisdictional restrictions, accreditation gating and holding-period locks — enforced at the token layer." },
              { I: Scale, t: "Regulatory Alignment", d: "Structured product design aligned with applicable securities, funds and virtual-asset frameworks in each market." },
              { I: ScrollText, t: "Reporting & Audit", d: "Immutable audit trails, periodic third-party attestations, and reporting APIs for tax and regulatory needs." },
              { I: Radio, t: "Ongoing Monitoring", d: "Continuous risk scoring, anomaly detection and incident response with formal escalation processes." },
              { I: Users, t: "Governance", d: "Institutional oversight through compliance committees, external counsel and formal risk-management frameworks." },
            ]} />
          </div>
        </Section>

        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Investor Eligibility" title="Clear eligibility gating on every offering" sub="Each offering discloses the applicable investor category — enforced at onboarding and at the token contract layer." />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["Retail (KYC)", "Open to KYC-verified retail investors with appropriateness checks and jurisdictional gating."],
                ["Accredited", "Requires accreditation attestation, income/net-worth thresholds and appropriateness suitability."],
                ["Qualified", "Institutional qualification (professional client, family office, treasury) with expanded product access."],
                ["Institutional", "Direct-relationship coverage, KYB and structured onboarding with dedicated compliance dialogue."],
              ].map(([t, d], i) => (
                <Reveal key={t} i={i}>
                  <div className="glass rounded-2xl p-6 h-full">
                    <h4 className="font-medium mb-2">{t}</h4>
                    <p className="text-sm text-secondary-auvra leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        <CtaBar primary="Start KYC" primaryHref="/signup" secondary="Contact Compliance" secondaryHref="/contact" />
      </main>
      <Footer />
    </>
  );
}

// ========================================
//  RESOURCES
// ========================================
export function Resources({ onCta }) {
  const links = [
    { I: BookOpen, t: "Whitepaper", d: "Full technical and strategic foundation of AUVRA RWA.", href: "/whitepaper", tag: "In-app + PDF" },
    { I: FileText, t: "Institutional Master Manual", d: "Governance, compliance, technology and operational profile.", href: "/docs/AUVRA-Institutional-Master-Manual.pdf", external: true, tag: "PDF" },
    { I: Coins, t: "AUR Token Page", d: "Contract, tokenomics, utilities, security and governance for AUR.", href: "/aur-token", tag: "In-app" },
    { I: Users, t: "About AUVRA", d: "Team, headquarters, story, mission, values and roadmap.", href: "/about", tag: "In-app" },
    { I: Handshake, t: "Partnerships", d: "Engagement models for asset managers, sponsors and fintechs.", href: "/partnerships", tag: "In-app" },
    { I: ShieldCheck, t: "Compliance Framework", d: "Identity, on-chain transfer rules, eligibility and reporting.", href: "/compliance", tag: "In-app" },
  ];
  return (
    <>
      <Navbar onCta={onCta} />
      <main>
        <Hero kicker="Resources" title="Documentation," blue="research & insights"
          sub="Everything you need to understand, integrate and build with the AUVRA platform." />
        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Library" title="Start here" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {links.map((l, i) => {
                const inner = (
                  <div className="glass rounded-2xl p-8 h-full hover:border-auvra-blue/30 transition-colors group">
                    <l.I className="w-6 h-6 text-auvra-blue mb-5" />
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-lg font-medium">{l.t}</h4>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/10 text-secondary-auvra">{l.tag}</span>
                    </div>
                    <p className="text-sm text-secondary-auvra leading-relaxed">{l.d}</p>
                    <div className="mt-6 flex items-center gap-2 text-sm text-auvra-blue">Open <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></div>
                  </div>
                );
                return (
                  <Reveal key={l.t} i={i % 3}>
                    {l.external ? <a href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`res-${l.t.replace(/\s/g, "-").toLowerCase()}`}>{inner}</a>
                                 : <Link to={l.href} data-testid={`res-${l.t.replace(/\s/g, "-").toLowerCase()}`}>{inner}</Link>}
                  </Reveal>
                );
              })}
            </div>
          </div>
        </Section>

        <Section>
          <div className="container-auvra">
            <SectionHeader kicker="Insights" title="Perspectives on tokenized markets" />
            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["Institutional Adoption of RWA", "How compliance-first infrastructure unlocks institutional participation in tokenized real assets.", "Research"],
                ["Programmable Distributions", "The engineering behind on-chain distribution schedules and secondary market plumbing.", "Engineering"],
                ["Custody & On-Chain Trust", "Multi-signature treasury design, institutional custody integrations and operational security.", "Security"],
                ["Regulatory Roadmap", "Where global RWA frameworks are heading — and how AUVRA aligns.", "Policy"],
              ].map(([t, d, tag], i) => (
                <Reveal key={t} i={i}>
                  <div className="glass rounded-2xl p-8 h-full">
                    <div className="flex items-center gap-2 mb-3">
                      <Newspaper className="w-4 h-4 text-auvra-blue" />
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/10 text-secondary-auvra">{tag}</span>
                    </div>
                    <h4 className="text-lg font-medium">{t}</h4>
                    <p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        <Section>
          <div className="container-auvra">
            <div className="glass rounded-2xl p-8 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold tracking-tight">Downloadable documents</h3>
                <p className="text-sm text-secondary-auvra mt-1">Whitepaper and Institutional Master Manual as PDF.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="/docs/AUVRA-RWA-Whitepaper.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all"><Download className="w-4 h-4" /> Whitepaper</a>
                <a href="/docs/AUVRA-Institutional-Master-Manual.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all"><Download className="w-4 h-4" /> Master Manual</a>
              </div>
            </div>
          </div>
        </Section>

        <CtaBar />
      </main>
      <Footer />
    </>
  );
}
